import stableStringify from "json-stable-stringify";
import { Field, JsonParseError, JsonParseErrorCode, RawJsonField, Regexp } from "sparkson";
import { StringHashCodeGenerator } from "../../utils/StringHashCodeGenerator";
import { VisibilityRange } from "./VisibilityRange";

export class CTA {
  hashCode: number;

  constructor(
    @Field("id") public id: number,
    @Field("t") public type: string,
    @Field("tt") @Regexp(/^(RANGE|END)$/) public triggerType: "RANGE" | "END",
    @Field("p", true) public poster?: string,
    @RawJsonField("c", true) public customAttributes?: any,
    @Field("re", true) public remarketingData?: string,
    @Field("vr", true) public visibilityRange?: VisibilityRange,
    @Field("sp", true) public stopsPlayback: boolean = true,
  ) {
    this.validate();
    this.hashCode = StringHashCodeGenerator.hashCode(stableStringify(this.customAttributes || {}) + this.type);
  }

  private validate() {
    if (this.triggerType === "RANGE" && !this.visibilityRange) {
      throw new JsonParseError('visibilityRange has to be specified for "RANGE" triggerType', JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (this.poster && this.triggerType !== "END") {
      throw new JsonParseError("Posters can be defined only for CTAs with triggerType 'END'", JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (this.type === "clickableArea" || this.type === "clickableButton") {
      this.validateClickableAreaCustomAttributes();
    }
  }

  private validateClickableAreaCustomAttributes() {
    if (this.type === "clickableButton") {
      ["color", "bgrcolor", "buttontext", "textsize"].forEach((attr) => this.validateClickableButtonStringAttributes(attr));
      ["rounded", "outline"].forEach((attr) => this.validateClickableButtonBooleanAttributes(attr));
    }

    if (!this.customAttributes) {
      throw new JsonParseError("Missing attributes for clickable area cta", JsonParseErrorCode.VALIDATION_FAILED);
    }

    ["top", "bottom", "left", "right"].forEach((dimensionName) => this.validateClickableAreaDimension(dimensionName));

    const scenarioElementId = this.customAttributes.scenarioElementId;
    if (scenarioElementId !== undefined) {
      if (typeof scenarioElementId !== "string") {
        throw new JsonParseError("Clickable area cta 'scenarioElementId' attribute must be a string", JsonParseErrorCode.VALIDATION_FAILED);
      }
    }

    const url = this.customAttributes.url;
    if (url) {
      if (typeof url !== "string") {
        throw new JsonParseError("Clickable area cta 'url' attribute must be a url", JsonParseErrorCode.VALIDATION_FAILED);
      }
    }

    const scenarioElementIdPresent = scenarioElementId !== undefined;
    const urlPresent = url !== undefined;
    if (scenarioElementIdPresent === urlPresent) {
      throw new JsonParseError(
        "Clickable area must have either 'scenarioElementId' or 'url' attribute ",
        JsonParseErrorCode.VALIDATION_FAILED,
      );
    }
  }

  private validateClickableAreaDimension(dimensionName: string) {
    const value = this.customAttributes[dimensionName];
    if (value === undefined) {
      throw new JsonParseError(`Clickable area cta must have '${dimensionName}' attribute`, JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (typeof value !== "string") {
      throw new JsonParseError(`Clickable area cta '${dimensionName}' attribute must be a string`, JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (parseInt(value, 10) < 0 || parseInt(value, 10) > 100) {
      throw new JsonParseError(
        `Clickable area cta '${dimensionName}' attribute must be from interval [0, 100]`,
        JsonParseErrorCode.VALIDATION_FAILED,
      );
    }
  }
  private validateClickableButtonStringAttributes(attr: string) {
    const value = this.customAttributes[attr];
    if (value === undefined) {
      throw new JsonParseError(`Clickable button cta must have '${attr}' attribute`, JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (typeof value !== "string") {
      throw new JsonParseError(`Clickable cta '${attr}' attribute must be a string`, JsonParseErrorCode.VALIDATION_FAILED);
    }
  }
  private validateClickableButtonBooleanAttributes(attr: string) {
    const value = this.customAttributes[attr];
    if (value === undefined) {
      throw new JsonParseError(`Clickable button cta must have '${attr}' attribute`, JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (typeof value !== "boolean") {
      throw new JsonParseError(`Clickable area cta '${attr}' attribute must be a boolean`, JsonParseErrorCode.VALIDATION_FAILED);
    }
  }
}
